import { LabelValueLower } from './../../models/keyvalue';
import { LazyLoadEvent } from 'primeng/api';
import { Observable } from 'rxjs';


export interface IGridConfig<TItem> {
    scrollHeight: string;
    getPageFunc: (paging: PagingQuery) => Observable<any>;
    columns:  GridCustomColumn<TItem>[];
    buildSmartActionFunc: (paging: PagingQuery) => any;
    gridFilters: GridFilterItem[];
}

export abstract class BaseGridDataResult {
    lastItemPosition: any;
    firstItemPosition: number;
    items: any[];
    // abstract getItems(): any[];
}

export abstract class GridDataResult<T> extends BaseGridDataResult {
    lastItemPosition: any;
    firstItemPosition: number;
    items: T[];
    // abstract getItems(): any[];
}
export class GridCustomColumn<T> {
    displayName: string;
    property?: string;
    valueFunc?: (item: T) => any;
    sortable: string | boolean;
    styleClass: string;
    filter: boolean;
    colspan?: number;
    hideOnLimitedDisplay?: boolean = false;
}

export class GridFilterItem {
    property: GridFilterProperty;
    operator: GridFilterPropertyOperator;
    value: any;
    editing: boolean;
    isNew: boolean;
    valid: boolean;
    valueLabel: string;

    get ready() {
        return this.valid && this.property && this.operator && this.value;
    }

    public constructor(init?: Partial<GridFilterItem>) {
        Object.assign(this, init);
    }
}

export class GridFilterItemFinal {

    property: string;
    operator: string;
    value: string;
    isCollection: boolean;
    type: 'DateTime';

    constructor(property: string, partial?: Partial<GridFilterItemFinal>);
    constructor(original: Partial<GridFilterItem>);
    constructor(key: any, partial?: Partial<GridFilterItemFinal>) {
        if (typeof key === 'string') {
            Object.assign(this, partial);
            this.property = key as string;
        } else {
            const original = key as GridFilterItem;
            this.property = original.property && original.property.name;
            this.operator = original.operator;
            this.isCollection = original.property && original.property.isCollection;
            if (original.value) {
                if (original.value instanceof Array) {
                    this.value = (original.value as any[]).map(item => item.value).join(' | ');
                } else {
                    this.value = (original.value as string).toUpperCase();
                }
            }
        }
    }
}

export class GridFilterProperty {
    name: string;
    displayName?: string;
    displayType?: GridFilterPropertyDisplayType;
    value?: LabelValueLower[];
    valueFunc?: () => { label: string, value: string }[];
    valueFuncAsync?: () => Observable<{ label: string, value: string }[]>;
    allowedOperators?: GridFilterPropertyOperator[];
    changeFunc: (filterItem: GridFilterItem) => Promise<void>;
    isCollection: boolean;

    get valueProviderType() {
        if (this.value != null) {
            return 'value';
        } else if (this.valueFunc != null) {
            return 'valueFunc';
        } else if (this.valueFuncAsync != null) {
            return 'valueFuncAsync';
        } else {
            return null;
        }
    }

    constructor(name: string, displayName: string, allowedOperators: GridFilterPropertyOperator[], displayType: GridFilterPropertyDisplayType = null, isCollection: boolean = false) {
        this.name = name;
        this.displayName = displayName;
        this.allowedOperators = allowedOperators;
        this.displayType = displayType;
        this.isCollection = isCollection;
    }

    setValue(value: any) {
        this.value = value;
        return this;
    }

    setValueFunc(valueFunc: () => { label: string, value: string }[]) {
        this.valueFunc = valueFunc;
        return this;
    }

    setValueFuncAsync(valueFuncAsync: () => Observable<{ label: string, value: string }[]>) {
        this.valueFuncAsync = valueFuncAsync;
        return this;
    }

    setChangeFunc(changeFunc: () => Promise<void>) {
        this.changeFunc = changeFunc;
        return this;
    }
}

export type GridFilterPropertyDisplayType = 'select' | 'multiselect' | 'input';
export type GridFilterPropertyOperator = 'equal to' | 'not equal to' | 'starts with';

export class GridFilterItemEvent {
    item: GridFilterItem;
    on: string;
}


export class OnGridFilterEvent {
    eventType: EventType;
    item: GridFilterItem;
    full: GridFilterItem[];
}

export type EventType = 'updating' | 'updated' | 'deleted' | 'cancel-editing';



export class AzurePageResult<T> extends GridDataResult<T> {
    '@search.coverage'?: any;
    '@search.facets'?: any;

    items: T[];
    totalCount: number;
    filteredCount: number;

    constructor(input: Partial<AzurePageResult<T>>) {

        super();
        Object.assign(this, input);
        const generic = input as any;

        if (input != null) {

            // Internal Pagination
            if (generic.value != null) {
                this.items = ((generic.value || []) as any[]).map(item => item.Document || item.document || item);
                this.totalCount = generic.Count || input['@odata.count'];
                this.filteredCount = generic.Count || input['@odata.count'];
            } else {
                this.items = ((input.items || []) as any[]).map(item => item.Document || item.document || item);
                this.totalCount = input.totalCount;
                this.filteredCount = input.filteredCount;
            }

            this['@search.coverage'] = input['@search.coverage'];
            this['@search.facets'] = input['@search.facets'];
        }
    }


    getItems() {
        return this.items;
    }

    getFilteredCount() {
        return this.filteredCount;
    }
}

export class PagingQuery {
    $first: number;
    $rows: number;
    $sort: SortItem[];

    constructor($event?: LazyLoadEvent) {
        const internal = $event || {} as LazyLoadEvent;
        this.$first = internal.first || 0;
        this.$rows = internal.rows || 10;
        this.$sort = PagingQuery.buildSort(internal);
    }


    static buildSort($event: LazyLoadEvent): SortItem[] {
        const result: SortItem[] = [];

        if ($event.sortField) {
            result.push({ fieldName: $event.sortField, direction: $event.sortOrder } as SortItem);
        } else if ($event.multiSortMeta && $event.multiSortMeta.length > 0) {
            $event.multiSortMeta.forEach(sort => result.push({ fieldName: sort.field, direction: sort.order } as SortItem));
        }

        return result;
    }

    getParameters() {
        const params = { first: this.$first, rows: this.$rows };
        return this.concactQueryParams(params);
    }

    concactQueryParams(input: any) {
        const queryParamsString = Object.keys(input).map(key => key + '=' + input[key]).join('&');
        return queryParamsString;
    }

}

export class PagingQueryGeneric<T> extends PagingQuery {
    baseFilter: T;

    constructor(filter: T, paging?: PagingQuery) {
        super();
        this.baseFilter = filter;

        const internalPaging = paging;
        this.$first = internalPaging.$first;
        this.$rows = internalPaging.$rows;
        this.$sort = internalPaging.$sort;
    }
}

export interface SortItem {
    fieldName: string;
    direction: number;
}

export interface IFacetItem {
    name: string;
    value: IFacetItemValue[];
}

export interface IFacetItemValue {
    from: any;
    to: any;
    type: any;
    value: string;
    count: number;
}

export interface IFacetQueryItem {
    property: string;
    maxCount?: number;
}


export class SortDirections {
    static DESC = -1;
    static ASC = 1;
}


export interface AzureDocumentResult<T> {
    Document: T;
}




export interface IFacets {
    '@search.facets'
}
